import type { Container } from "pixi.js";
import type { ReplayPlugin } from "@slot/client-engine";
import { CannonPresenter } from "./CannonPresenter";
import type { CannonView } from "./CannonView";
import type { CannonBulletView } from "./CannonBulletView";
import { CANNON_LADDER } from "./cannonTuning";
import { ApplyWinMultiplierActionHandler, type WinMultiplierPresentationTarget } from "./replay/ApplyWinMultiplierActionHandler";
import { WIN_PRESENTATION_TUNING } from "../../tuning/winPresentationTuning";
import { ShineSweepSymbolWinEffect } from "@slot/presentation";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import type { GameFeature } from "@slot/game-shell";

export interface CannonFeatureDeps {
  /** The cannon view; null → no presenter (the win label still merges the multiplier). */
  view: CannonView | null;
  /** Builds one cannon round (chamber/queue bullets + the loading round). */
  createBullet: (() => CannonBulletView) | null;
  runner: PresentationTaskRunner;
  playSound: (soundId: string) => void;
  log: Log;
}

/**
 * The cannon (win multiplier with a revealed queue): a `CannonPresenter` over the scene's cannon
 * view, driven by the `apply_win_multiplier` action through `cannonMultiplierTarget`: the shot
 * lands on the win the shell's `WinSequencePresenter` holds at its base (decision 46).
 */
export const cannonFeature: GameFeature<CannonFeatureDeps, CannonPresenter | null, WinMultiplierPresentationTarget> = {
  createPresenter(deps) {
    if (!deps.view) return null;
    // Silent-reload glint: a shine-sweep across every seated round (plate + digits). Its own effect
    // instance (once:false — every reload re-sweeps), same tuning as the win sweep.
    const sweep = new ShineSweepSymbolWinEffect({ tuning: WIN_PRESENTATION_TUNING.shineSweep, taskRunner: deps.runner });
    return new CannonPresenter({
      view: deps.view,
      createBullet: deps.createBullet,
      queueShine: (bulletRoot: Container) => sweep.sweepNode(bulletRoot),
      baseLadder: CANNON_LADDER,
      runner: deps.runner,
      playSound: deps.playSound,
      log: deps.log,
    });
  },

  createPlugin(target): ReplayPlugin {
    return { name: "cannon", actionHandlers: [new ApplyWinMultiplierActionHandler(target)] };
  },
};

/**
 * The `apply_win_multiplier` target: the chambered shot fires and, as it lands, the win labels the win sequence held at
 * their base morph "base" → "base × multiplier" → total. The merge does not wait for the shot to end: it runs in parallel
 * with the removal that follows, so the blast and the symbols clearing happen together (the server always authors the
 * fire before the removal, so the labels are still up). Without a cannon view the labels merge at once.
 */
export function cannonMultiplierTarget(presenter: CannonPresenter | null, mergeWinLabels: () => void): WinMultiplierPresentationTarget {
  return {
    presentWinMultiplier: async (input) => {
      if (presenter) await presenter.presentWinMultiplier(input, mergeWinLabels);
      else mergeWinLabels();
    },
  };
}
